//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import { getImpactConnection } from '@apis/account-settings/impactAccountApi';
import {
  getAdobeOAuthSettings,
  getAdobeWebServiceSettings,
  getCuralatePixelSettings,
  getGA4Settings,
  getGASettings,
} from '@apis/analytics-settings/analyticsSettingsApi';
import {
  IGetAdobeSettingsResponse,
  IGetCuralatePixelSettingsResponse,
  IGetGA4SettingsResponse,
  IGetGASettingsResponse,
} from '@apis/analytics-settings/analyticsSettingsApiModel';
import { getConnectedFacebookPageToken } from '@apis/facebook-settings/gatewayFacebookSettingsApi';
import {
  getConnectedInstagramAccount,
  getConnectedInstagramAccountsMessagesAccessStatus,
} from '@apis/instagram-settings/gatewayInstagramSettingsApi';
import {
  GetConnectedInstagramAccountsMessagesAccessStatusResponse,
  IGetConnectedInstagramAccountResponse,
} from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { getUser } from '@apis/pinterest-user/pinterestUserApi';
import { getOverviewItems } from '@apis/product-feed-health/productFeedHealthApi';
import { IProductFeedHealthOverviewResponse } from '@apis/product-feed-health/productFeedHealthApiModels';
import { getTikTokUser } from '@apis/tiktok-user/tiktokUserApi';
import { getTwitterSettings } from '@apis/twitter-settings/twitterSettingsApi';
import { getUser as getTwitterUser } from '@apis/twitter-user/twitterUserApi';
import { IGetUserRequest } from '@apis/twitter-user/twitterUserApiModel';
import { getUserSettings } from '@apis/user-settings/userSettingsApi';
import { getYouTubeUser } from '@apis/youtube/youtubeApi';
import { YouTubeUser } from '@apis/youtube/youtubeApiModel';
import { IUserSettings } from '@models/IUserSettings';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { isTruthy } from '@utils/filterPredicates';

export const useConnectedInstagramAccountQuery = <
  TData = IGetConnectedInstagramAccountResponse,
>(
  select?: (data: IGetConnectedInstagramAccountResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialInstagramAccount,
    ],
    queryFn: async ({ signal }) => getConnectedInstagramAccount({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useCurrentUserSettingsQuery = <TData = IUserSettings>(
  select?: (data: IUserSettings) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsCurrentUser,
    ],
    queryFn: async ({ signal }) => getUserSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useProductFeedHealthOverviewQuery = <
  TData = IProductFeedHealthOverviewResponse,
>(
  select?: (data: IProductFeedHealthOverviewResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsProductFeedHealthOverview,
    ],
    queryFn: async ({ signal }) => getOverviewItems({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useGoogleAnalyticsSettingsQuery = <TData = IGetGASettingsResponse>(
  select?: (data: IGetGASettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleAnalytics,
    ],
    queryFn: async ({ signal }) => getGASettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useGoogleAnalytics4SettingsQuery = <
  TData = IGetGA4SettingsResponse,
>(
  select?: (data: IGetGA4SettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleAnalytics4,
    ],
    queryFn: async ({ signal }) => getGA4Settings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useAdobeWebServiceSettingsQuery = <
  TData = IGetAdobeSettingsResponse,
>(
  select?: (data: IGetAdobeSettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsAdobe,
      QueryKeyConstants.AccountSettingsAnalyticsAdobeWebService,
    ],
    queryFn: async ({ signal }) => getAdobeWebServiceSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useAdobeOAuthSettingsQuery = <TData = IGetAdobeSettingsResponse>(
  select?: (data: IGetAdobeSettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsAdobe,
      QueryKeyConstants.AccountSettingsAnalyticsAdobeOAuth,
    ],
    queryFn: async ({ signal }) => getAdobeOAuthSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useVibePixelSettingsQuery = <
  TData = IGetCuralatePixelSettingsResponse,
>(
  select?: (data: IGetCuralatePixelSettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsVibePixel,
    ],
    queryFn: async ({ signal }) => getCuralatePixelSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useConnectedInstagramAccountsMessagesAccessStatusQuery = <
  TData = GetConnectedInstagramAccountsMessagesAccessStatusResponse,
>(
  select?: (
    data: GetConnectedInstagramAccountsMessagesAccessStatusResponse,
  ) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialInstagramAccount,
      QueryKeyConstants.AccountSettingsSocialInstagramAccountMessagesAccessStatus,
    ],
    queryFn: async ({ signal }) =>
      getConnectedInstagramAccountsMessagesAccessStatus({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useConnectedFacebookPageTokenQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialFacebook,
    ],
    queryFn: () => getConnectedFacebookPageToken(),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export function usePinterestUserQuery(isPinterestConnected: boolean) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialPinterestUser,
    ],
    queryFn: async ({ signal }) => {
      const user = await getUser({ signal });
      return user ?? null;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isPinterestConnected,
  });
}

export function useGetYouTubeUserQuery<TData = YouTubeUser>(
  select?: (data: YouTubeUser) => TData,
) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialYouTubeUser,
    ],
    queryFn: async ({ signal }) => {
      return getYouTubeUser({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    select,
  });
}

export const useTikTokUserQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialTikTokUser,
    ],
    queryFn: async ({ signal }) => {
      return getTikTokUser({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useTwitterSettingsQuery = () => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialTwitter,
      QueryKeyConstants.AccountSettingsSocialTwitterSettings,
    ],
    queryFn: async ({ signal }) => {
      return getTwitterSettings({ signal });
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useTwitterUserQuery = (twitterUsername: string | undefined) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialTwitter,
      QueryKeyConstants.AccountSettingsSocialTwitterUser,
      twitterUsername,
    ],
    queryFn: async ({ signal }) => {
      if (!twitterUsername) {
        throw new Error('Twitter username is required');
      }
      const req: IGetUserRequest = { twitterUsername };
      const user = await getTwitterUser(req, { signal });

      return user ?? null;
    },
    staleTime: FIVE_MINUTES_STALE_TIME,
    enabled: isTruthy(twitterUsername),
  });
};

export const useImpactConnectionQuery = ({
  enabled,
}: { enabled?: boolean } = {}) => {
  return useQuery({
    enabled,
    queryFn: async ({ signal }) => getImpactConnection({ signal }),
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsImpact,
      QueryKeyConstants.AccountSettingsImpactConnection,
    ],
    retry: false,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
